<template>
    <Incremental
        :quantity-text="quantityText"
        :in-group="inGroup"
        :item-description="itemDescription"
        :item-pricing="itemPricing"
        :item-id="item.itemId"
        :included="included"
        :yearly-price="yearlyPricing"
        :subtract-disabled="subtractDisabled"
        :learn-more="item.metadata.learnMore"
        :item-type="attachmentType"
        :add-disabled="addDisabled"
        @subtract-item="subtract"
        @increment-item="add"
        v-if="shouldShow"
    />
</template>

<script>
import Incremental from '@/BuyNow/Components/AttachmentCards/Incremental.vue';
import { getBestTier, getTierKeyByMaxUnits, pluralize } from '@/Util/helpers.js';

export default {
    data() {
        return {
            selectedTier: 0,
        };
    },
    components: {
        Incremental
    },
    mounted() {
        if (getTierKeyByMaxUnits(this.pricingObject.tiers, this.quantity) - 1 < 0) {
            this.selectedTier = -1;
        } else {
            this.selectedTier = getTierKeyByMaxUnits(this.pricingObject.tiers, this.quantity) - 1;
        }

        if ((this.item.type == 'mandatory' && this.mandatoryItem) && (this.selectedTier <= this.mandatoryItem.quantity - 1)) {
            this.selectedTier = this.mandatoryItem.quantity - 1;
        }
    },
    props: {
        inGroup: {
            type: Boolean,
            default: false,
        },
        parentAdded: {
            type: Boolean,
            default: false,
        },
        item: Object,
        attachmentType: String
    },
    methods: {
        async add() {
            if (!this.addDisabled) {
                if (this.selectedTier >= this.pricingObject.tiers.length - 1) {
                    this.selectedTier = this.pricingObject.tiers.length - 1;
                } else {
                    this.selectedTier += 1;
                }
                if (!this.lineItems.find(item => item.itemId === this.item.itemId) && this.item.type != 'mandatory') {
                    this.selectedTier = 0;
                }
                if (this.lineItems.find(item => item.itemId === this.item.itemId)) {
                    await this.$store.commit('updateAttachment', { itemId: this.item.itemId, quantity: this.pricingObject.tiers[this.selectedTier].maxUnits });
                } else {
                    await this.$store.commit('addAttachment', { itemId: this.item.itemId, quantity: this.pricingObject.tiers[this.selectedTier].maxUnits });
                }

                if (this.$store.getters.hasFetchedEstimate && this.$store.getters.shouldFetchEstimate) {
                    await this.$store.dispatch('fetchEstimate');
                }
            }
        },
        async subtract() {
            if (!this.subtractDisabled && this.lineItems.find(item => item.itemId === this.item.itemId)) {
                this.selectedTier -= 1;
                if (
                    this.selectedTier < 0
                ) {
                    await this.$store.commit('removeAttachment', this.item.itemId);
                } else {
                    await this.$store.commit('updateAttachment', { itemId: this.item.itemId, quantity: this.pricingObject.tiers[this.selectedTier].maxUnits });
                }
                if (this.$store.getters.hasFetchedEstimate && this.$store.getters.shouldFetchEstimate) {
                    await this.$store.dispatch('fetchEstimate');
                }
            }
        },
    },
    computed: {
        mandatoryItem() {
            return this.$store.getters.selectedPlan[this.attachmentType + 's'].find(item => item.itemId == this.item.itemId);
        },
        shouldShow() {
            if (!!this.item.metadata.group && !this.item.metadata.group.parent) {
                return this.parentAdded;
            }
            return true;
        },
        included() {
            if (this.item.type === 'mandatory' && this.selectedTier == 0) {
                return true;
            }
            return false;
        },
        addDisabled() {
            if (this.$store.getters.isSoftLoading) {
                return true;
            }
            if (this.item.itemId.toLowerCase().includes('text-voice') && !!this.item.metadata.tiers) {
                return !this.item.metadata.tiers[this.selectedTier + 1] || !this.item.metadata.tiers[this.selectedTier + 1].showInCart;
            }
            return this.pricingObject.tiers[this.selectedTier + 1] && !this.pricingObject.tiers[this.selectedTier + 1].maxUnits;
        },
        subtractDisabled() {
            if (this.$store.getters.isSoftLoading) {
                return true;
            }
            return (this.item.type === 'mandatory' && this.selectedTier + 1 == this.mandatoryItem.quantity) || !this.lineItems.find(item => item.itemId === this.item.itemId);
        },
        lineItems() {
            return [
                ...this.$store.state.cartInfo.selectedAddons,
                ...this.$store.state.cartInfo.selectedCharges,
            ];
        },
        selectedCharge() {
            return this.$store.state.cartInfo.selectedCharges.find(item => {
                return item.itemId == this.item.itemId;
            });
        },
        selectedAddon() {
            return this.$store.state.cartInfo.selectedAddons.find(item => {
                return item.itemId == this.item.itemId;
            });
        },
        selectedPricing() {
            if (!this.lineItems.find(item => item.itemId === this.item.itemId)) {
                return 0;
            } else {
                return getBestTier(this.pricingObject.tiers, this.quantity).unitPrice;
            }
        },
        pricingObject() {
            let found;
            if (this.item.pricing.length > 1) {
                found = this.item.pricing.find((price) => {
                    return price.billingPeriodUnit === this.$store.getters.cartInfo.billingPeriod;
                });
            } else {
                found = this.item.pricing[0];
            }
            return found;
        },
        quantityText() {
            // Text & voice edge case
            if (this.item.itemId.toLowerCase().includes('text-voice') && !!this.item.metadata.tiers) {
                let tiers = this.item.metadata.tiers;
                let includedMessages = this.selectedTier >= 0 ? tiers[this.selectedTier].includedMessages : 0;
                let includedMinutes = this.selectedTier >= 0 ? tiers[this.selectedTier].includedMinutes : 0;
                return `${includedMessages.toLocaleString()} messages & ${includedMinutes.toLocaleString()} minutes`;
            }
            // End text & voice edge case

            return `${this.quantity.toLocaleString()} ${pluralize(this.unit, this.quantity)}`;
        },
        quantity() {
            if (!this.lineItems.find(item => item.itemId === this.item.itemId)) {
                return 0;
            } else {
                let tier = this.lineItems.find(item => item.itemId === this.item.itemId);
                return tier.quantity;
            }
        },
        unit() {
            let unit = this.item.metadata.unitTitle || this.item.metadata.unitOfMeasure || this.item.unit || this.item.metadata.title || this.item.name;
            if (unit && unit.includes('|')) {
                unit = unit.split('|');
                return unit[0];
            }
            return unit;
        },
        itemPricing() {
            if (this.attachmentType === 'charge') {
                return this.selectedPricing / 100;
            }else if (this.$store.getters.isYearly) {
                return Math.round(((this.selectedPricing / 100) / 12) * 100) / 100
            } else {
                return this.selectedPricing / 100;
            }
        },
        yearlyPricing() {
            return this.selectedPricing / 100;
        },
        itemName() {
            return this.item.metadata.title || this.item.name;
        },
        itemDescription() {
            return this.item.metadata.description || this.item.description;
        },
    },
    watch: {
    }
};
</script>

<style lang="postcss" scoped>
</style>
